import * as AppConstants from 'app/constants/AppConstants';
import TimeController from 'app/controller/TimeController';
import ButtonSimple from 'app/ui/buttons/ButtonSimple';
import MapController from 'app/game/controllers/MapController';
import CollisionController from 'app/game/controllers/CollisionController';
import TapArea from 'app/game/TapArea';
import GameTutorialController from '../game/controllers/GameTutorialController';
import Hero from '../game/sprites/Hero';

import HudLevelCountdown from 'app/ui/hud/HudLevelCountdown';

import Row from '../game/display/Row';
import { GAME_END_HEIGHT, GAME_START_HEIGHT, GAME_ROAD_HEIGHT, GAME_ROAD_WIDTH } from '../constants/AppConstants';
import { GAME_SFX_PATTY } from 'app/constants/GameSFX';
import WarningDisplay from '../ui/display/WarningDisplay';

class Game extends Phaser.State {
  init({ levelData, showTutorial, playerLevel }) {
    this.levelData = levelData;
    this.playerLevel = playerLevel;
    this.game.log('Start Level', 'level : ' + this.playerLevel, this.levelData);

    this.laneCount = this.levelData.laneCount;
    this.rowGap = 120;
    this.rows = [];
    this.currentLane = 0;
    this.controlsBuffer = [];

    this.game.controller.gameModel.isCharacterAvailable = true; // used to check if 2nd character can be placed in trolley

    this.game.sounds.playGameMusic();

    this.gameContainer = this.game.add.group();
  }

  create(game) {
    this.game = game;

    this.game.sceneHeight = GAME_END_HEIGHT + GAME_START_HEIGHT + (this.laneCount / 2) * GAME_ROAD_HEIGHT;
    this.game.rowCount = this.laneCount;

    //Create all controllers needed for the game
    this.createControllers();

    //Create all the listeners needed
    this.createListeners();

    this.createScene();

    // show tutorial first time
    if (this.game.controller.gameModel.tutorialComplete == false) {
      this.game.hud.showInstructions();
      this.game.hud.gameInstructions.onClose.add(this.handleTutorialComplete, this, 1);

      this.updatePauseStatus({ paused: true, fromUI: false, stopMusic: false });
    }

    this.handleStartGame();
  }

  createControllers() {
    //Create a collision controller
    this.collisionController = new CollisionController({ game: this.game, debug: false });

    //If the collision debugger is active, add it to the scene
    if (this.collisionController.debugger) this.game.add.existing(this.collisionController.debugger);

    // Create a time controller
    this.timeController = new TimeController(this.game);

    this.mapController = new MapController({
      game: this.game,
      levelData: this.levelData,
      playerLevel: this.playerLevel,
    });

    //this.gameContainer.add(this.mapController.roadLanes);
    this.gameContainer.addChild(this.mapController);
    this.gameContainer.addChild(this.mapController.objects);
  }

  createListeners() {
    //Create pause game listener
    this.game.controller.uiModel.onPauseIsChanged.add(this.updatePauseStatus, this);
    this.isPaused = false;

    //Create an area to listen for tap input
    this.tapArea = new TapArea({ game: this.game, width: this.game.width, height: this.game.height, debug: false });
    // add listeners
    this.tapArea.onSwipeUp.add(this.handleUserInputUp, this);
    this.tapArea.onSwipeLeft.add(this.handleUserInputLeft, this);
    this.tapArea.onSwipeRight.add(this.handleUserInputRight, this);
    //Fix to camera so it's always on screen
    this.tapArea.fixedToCamera = true;
    this.game.add.existing(this.tapArea);

    this.bindedWindowBlur = this.handleWindowBlur.bind(this);
    window.addEventListener('blur', this.bindedWindowBlur);

    // keyboards
    this.upKey = this.game.input.keyboard.addKey(Phaser.Keyboard.UP);
    this.upKey.onDown.add(this.handleUserInputUp, this);

    this.leftKey = this.game.input.keyboard.addKey(Phaser.Keyboard.LEFT);
    this.leftKey.onDown.add(this.handleUserInputLeft, this);

    this.rightKey = this.game.input.keyboard.addKey(Phaser.Keyboard.RIGHT);
    this.rightKey.onDown.add(this.handleUserInputRight, this);

    //Create window resizing listener
    this.game.windowEventController.handleWindowResize();
    this.game.scale.onSizeChange.add(this.handleSizeChange, this);
    this.handleSizeChange();
  }

  disableControls() {
    this.tapArea.onSwipeUp.remove(this.handleUserInputUp, this);
    this.tapArea.onSwipeLeft.remove(this.handleUserInputLeft, this);
    this.tapArea.onSwipeRight.remove(this.handleUserInputRight, this);
    this.upKey.onDown.remove(this.handleUserInputUp, this);
    this.leftKey.onDown.remove(this.handleUserInputLeft, this);
    this.rightKey.onDown.remove(this.handleUserInputRight, this);
  }

  createScene() {
    let character = this.game.controller.gameModel.getChosenCharacter();

    //Create the hero character
    this.game.hero = new Hero({ game: this.game, gameState: this, character }, this);

    //Set it's position to be in the middle of the screen
    const pos = this.mapController.gridToPosMiddle(0);
    this.game.hero.position.set(pos.x, pos.y);
    this.game.hero.currentColumn = this.mapController.getMidGrid(0);

    // add to group with all sorted objects
    this.mapController.objects.addChild(this.game.hero);

    //
    this.createCamera();
  }

  createCamera() {
    //Set the camera's bounds
    this.game.world.setBounds((this.game.width - GAME_ROAD_WIDTH) / 2, 0, GAME_ROAD_WIDTH, this.game.sceneHeight);
    //Set the camera to follow the hero character with a lerping function
    this.game.camera.follow(this.game.hero, Phaser.Camera.FOLLOW_LOCKON, 0.1, 0.1);
    //Create a deadzone to stop the camera from always moving (Keeps hero character locked to the bottom of the screen)
    //pd this.game.camera.deadzone = new Phaser.Rectangle(0, 600, GAME_ROAD_WIDTH, 1);
  }

  /*
  ====================================================================================
    HANDLERS / EVENTS
  ====================================================================================
  */

  handleStartGame() {
    this.gameActive = true;
    this.tapArea.activate();
    this.timeController.start();
  }

  handleIntroComplete() {
    this.handleStartGame();
  }

  handleUserInputUp() {
    if (!this.gameActive || this.isPaused) return;

    if (this.game.hero.currentRow === this.mapController.rows.length - 1) return;

    this.tryMove('up');
  }

  handleUserInputLeft() {
    if (!this.gameActive || this.isPaused) return;

    if (this.game.hero.currentColumn === 0) return;

    this.tryMove('left');
  }

  handleUserInputRight() {
    if (!this.gameActive || this.isPaused) return;

    if (this.game.hero.currentColumn === this.mapController.rows[0].cellsInRow - 1) return;

    this.tryMove('right');
  }

  handleRestart() {
    this.game.hero.currentRow = 0;
    this.game.controller.restartLevel();
  }

  handleLose() {
    if (!this.game.deviceController.isOldAppleDevice()) {
      //this.game.sounds.playSFX('lose');
    }

    this.disableControls();

    this.game.camera.follow(null);

    this.mapController.objects.bringToTop(this.game.hero);

    this.game.hero.handleLose();
    this.mapController.stopAnimations();
  }

  handleLevelComplete() {
    this.disableControls();

    this.game.hero.handleWin();

    this.mapController.endRoad.playPattyEnd();
    this.game.sounds.playSFX(GAME_SFX_PATTY);

    this.game.time.events.add(2600, this.handleWinAnimEnd, this);
  }

  handleWinAnimEnd() {
    this.game.hero.state = Hero.STATE_WIN;
    this.game.controller.showLevelEnd({ win: true, character: this.game.hero.character });
  }

  handleWindowBlur() {
    this.game.controller.pauseGame();
  }

  handleSizeChange() {
    let scale = this.game.width / this.game.defaultWidth;

    //this.gameContainer.scale.set(Math.max(1, scale));
    this.gameContainer.x = (this.game.width - GAME_ROAD_WIDTH) / 2;

    this.gameContainer.y = this.game.height - this.game.defaultHeight;
    this.game.world.setBounds((this.game.width - GAME_ROAD_WIDTH) / 2, 0, GAME_ROAD_WIDTH, this.game.sceneHeight);

    this.tapArea.hitArea.width = this.game.width;
    this.tapArea.hitArea.height = this.game.height;
  }

  handleWarningComplete() {
    this.updatePauseStatus({ paused: false });
    this.game.controller.gameModel.warningComplete = true;

    this.warningPanel.destroy();
    this.warningPanel = null;
  }

  handleTutorialComplete() {
    this.updatePauseStatus({ paused: false });
    this.game.controller.gameModel.tutorialComplete = true;
    this.game.hud.gameInstructions.onClose.remove(this.handleTutorialComplete, this);
  }

  /*
  ====================================================================================
    movement
  ====================================================================================
  */

  tryMove(dir) {
    if (this.game.hero.state == Hero.STATE_WALK) {
      if (this.controlsBuffer.length == 0) {
        this.controlsBuffer.push(dir);
      }
    } else {
      this.performMove(dir);
    }
  }

  performMove(dir) {
    let x, y, pos;
    switch (dir) {
      case 'up':
        x = this.game.hero.currentColumn;
        y = this.game.hero.currentRow + 1;

        // check if there is no shelf in destination
        if (this.mapController.canGoto(x, y)) {
          pos = this.mapController.gridToPos(x, y);
          this.game.hero.goto(pos.x, pos.y);
        }
        break;

      case 'left':
        x = this.game.hero.currentColumn - 1;
        y = this.game.hero.currentRow;

        // check if there is no shelf in destination
        if (this.mapController.canGoto(x, y)) {
          pos = this.mapController.gridToPos(x, y);
          this.game.hero.goto(pos.x, pos.y);
        }
        break;

      case 'right':
        x = this.game.hero.currentColumn + 1;
        y = this.game.hero.currentRow;

        // check if there is no shelf in destination
        if (this.mapController.canGoto(x, y)) {
          pos = this.mapController.gridToPos(x, y);
          this.game.hero.goto(pos.x, pos.y);
        }
        break;
    }
  }

  /*
  ====================================================================================
    LOOP
  ====================================================================================
  */

  update() {
    if (this.isPaused || !this.gameActive) {
      return;
    }
    // update clock
    this.timeStep = this.timeController.update() / 1000;

    if (this.game.hero.state !== Hero.STATE_LOSE) {
      this.mapController.updateSorting();

      this.checkCollisions();

      this.mapController.updateMap(this.timeStep);
    }
  }

  checkCollisions() {
    if (this.game.hero.state == Hero.STATE_WALK || this.game.hero.state == Hero.STATE_LAND) {
      let collision = this.collisionController.checkCollisions(this.game.hero, this.mapController.getColliders(this.currentLane));
      if (collision.length > 0) {
        // lost
        this.handleLose();
      }
    }
  }

  checkLanding(currentLane) {
    this.currentLane = currentLane;
    //If the player is still jumping across traffic
    if (currentLane == this.laneCount) {
      //Player has won this level!
      this.handleLevelComplete();
    } else {
      // check if there is a move in buffer
      if (this.controlsBuffer.length > 0) {
        this.performMove(this.controlsBuffer.shift());
      }
    }
  }

  /*
  ======================================================================================================
    DEBUG RENDER
  ======================================================================================================
  */
  render() {
    // this.game.debug.cameraInfo(this.game.camera, 32, 256);
  }

  /*
  ======================================================================================================
    PAUSE / RESUME
  ======================================================================================================
  */

  updatePauseStatus({ paused, fromUI, stopMusic = true }) {
    if (fromUI && paused == false && this.warningPanel) {
      return;
    }
    this.isPaused = paused;

    console.log('PAUSED: ' + this.isPaused);

    if (this.isPaused) {
      this.timeController.handleGamePaused();
      this.mapController.stopAnimations();
      this.game.hero.updatePauseStatus(true);
      if (stopMusic) {
        //this.game.sounds.stopGameMusic();
      }
    } else {
      this.timeController.start();
      if (this.game.hero.state != Hero.STATE_LOSE) {
        this.mapController.startAnimations();
      }
      this.game.hero.updatePauseStatus(false);
      this.game.sounds.playGameMusic();
    }
  }

  /*
  ======================================================================================================
     END GAME
  ======================================================================================================
  */

  shutdown(game) {
    //remove listeneres
    this.game.scale.onSizeChange.remove(this.handleSizeChange, this);
    window.removeEventListener('blur', this.bindedWindowBlur);
    this.game.controller.uiModel.onPauseIsChanged.remove(this.updatePauseStatus, this);
    this.disableControls();

    this.game.hud.hideGamePlayHud();

    // easy destroy
    this.timeController.destroy(true);
    this.mapController.destroy(true);
    this.tapArea.destroy(true);

    this.game.hero.destroy(true);

    this.gameContainer.destroy(true);

    // nullify
    this.gameActive = false;

    this.collisionController = null;
    this.timeController = null;
    this.mapController = null;
    this.tapArea = null;
    this.jumpKey = null;
    this.game.hero = null;

    this.gameContainer = null;
  }
}

export default Game;
